import Channel, { ThreadChannel } from '@@types/Channel';
import { CHANNEL_ID } from '@@types/common';
import { FileDummyLog } from '@@types/Log';

import { ChannelType, MAX_PREVIEW_ABLE_IMAGE_SIZE, MODAL_NAMES } from '~/constants';
import i18n from '~/i18n';
import LogActions from '~/stores/actions/LogActions';
import ChannelLogsStore from '~/stores/ChannelLogsStore';
import { channelManager } from '~/stores/ChannelManager';
import { coachMarkStore } from '~/stores/CoachMarkStore';
import DetailUiState, { threadDetailUiState } from '~/stores/DetailUiState';
import { forwardLogStore } from '~/stores/ForwardLogStore';
import { globalSettingStore } from '~/stores/GlobalSettingStore';
import { meteringStore } from '~/stores/MeteringStore';
import { api } from '~/stores/providers/Api2';
import { promiseQueue } from '~/stores/providers/PromiseQueue';
import { replyLogStore } from '~/stores/ReplyLogStore';
import { threadChannelLogsStore } from '~/stores/ThreadChannelLogsStore';
import { uiState } from '~/stores/UiState';
import { userStore } from '~/stores/UserStore';
import { createFileDummyLogs } from '~/utils/channelLogsUtil';
import { convertBlobToFile, getImageSize, isGifImage, isImageType, isPreviewAbleSize } from '~/utils/fileLogUtil';
import fileSizeFilter from '~/utils/fileSizeFilter';
import { convertUint8ArrayToBlob } from '~/utils/fileUtil';
import { isArray, isNonNil, mapIndexed, rename } from '~/utils/fnUtil/fnUtil';
import { isEmpty } from 'lodash';
import { assoc, curry, forEach, mergeRight, omit, pipe, tap } from 'ramda';

import Actions from '../Actions';

class UploadActions {
  dropFiles: (channel: Channel, files: File[], parentChannel?: Channel) => void;
  selectFiles: (channel: Channel, files: File[], parentChannel?: Channel) => void;

  private logActions: LogActions;
  private detailUiState: DetailUiState;
  private channelLogsStore: ChannelLogsStore;

  private setFileUploadProgress = curry((logId: string, progress: number) => {
    this.channelLogsStore.updateLog({
      id: logId,
      _progress: progress,
    });
  });

  private openImageEditorModal = (
    channelId: CHANNEL_ID,
    type: ChannelType,
    file: File,
    size: { width: number; height: number },
    needCreateChannel: boolean
  ) => {
    uiState.openModal({
      name: MODAL_NAMES.IMAGE_EDITOR,
      args: { channelId, type, image: file, size, needCreateChannel },
    });
  };

  private openLimitEditingModal = async (channelId: CHANNEL_ID, files: File[], needCreateChannel: boolean) => {
    await this.actions.modalActions.openConfirm({
      description: i18n.t('upload.warn.maxSize', {
        defaultValue: '용량이 {{maxSize}} 이상인 이미지 파일은 편집 기능을 사용할 수 없습니다.',
        maxSize: fileSizeFilter(MAX_PREVIEW_ABLE_IMAGE_SIZE, 'mb', 'upper'),
      }),
    });
    this.createFileLogsAndUploadFiles(channelId, files, needCreateChannel);
  };

  constructor(
    private actions: Actions,
    {
      logActions,
      detailUiState,
      channelLogsStore,
    }: { logActions: LogActions; detailUiState: DetailUiState; channelLogsStore: ChannelLogsStore }
  ) {
    this.dropFiles = this.pasteFile;
    this.selectFiles = this.pasteFile;
    this.logActions = logActions;
    this.detailUiState = detailUiState;
    this.channelLogsStore = channelLogsStore;
  }

  get contentsChannelId() {
    return this.detailUiState.contentsChannelId;
  }

  private createFileLogsAndUploadFilesInCurrentChannel = (
    channelId: string,
    senderId: string,
    seq: number,
    files: File[]
  ) => {
    this.logActions.fetchLatestLogsIfNeeded(channelId).then(() => {
      pipe<any, any, any, any, any>(
        createFileDummyLogs({ channelId, senderId, seq }),
        tap(this.detailUiState.scrollToBottom),
        tap(this.detailUiState.whenCurrentChannel(channelId, this.channelLogsStore.mergeDummyLogs)),
        this.uploadFiles
      )(files);
    });
  };

  public pasteFile = (channel: Channel | ThreadChannel | null, fileOrFiles: File[] | File, parentChannel?: Channel) => {
    if (!this.actions.channelActions.canUploadChannel(parentChannel ?? channel)) return;
    const channelId = parentChannel?.channelId ?? channel.channelId;
    this.verifyMeteringOver(channelId).then(() => {
      const files: File[] = isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
      const validFiles = this.validateAndFilterMaxSize(files);
      this.startUploadAction(channel, validFiles, parentChannel);
    });
  };

  private verifyMeteringOver = async (channelId: CHANNEL_ID) => {
    const mainChannelId = channelManager.getMainChannelId(channelId);
    if (!meteringStore.hasUsage(mainChannelId)) {
      await this.actions.meteringActions.fetchAndSet(mainChannelId);
      this.actions.meteringActions.setChannelBanner(mainChannelId);
    }
    if (meteringStore.isOverChannel(mainChannelId)) {
      this.actions.modalActions.openOverUsageAlert({ channelId: mainChannelId });
      return Promise.reject();
    }
    return Promise.resolve();
  };

  private startUploadAction = (
    channel: { channelId: CHANNEL_ID; type: ChannelType } | null,
    files: File[],
    parentChannel?: Channel
  ) => {
    if (isEmpty(files)) {
      return;
    }
    const firstFile = files[0];

    if (files.length > 1 || !isImageType(firstFile) || isGifImage(firstFile)) {
      return this.createFileLogsAndUploadFiles(channel?.channelId, files, !!parentChannel);
    }

    if (!isPreviewAbleSize(firstFile)) {
      return this.openLimitEditingModal(channel?.channelId, files, !!parentChannel);
    }

    getImageSize(firstFile).then(
      (size) =>
        this.openImageEditorModal(
          channel?.channelId,
          channel?.type ?? ChannelType.Thread,
          firstFile,
          size,
          !!parentChannel
        ),
      () => this.createFileLogsAndUploadFiles(channel?.channelId, files, !!parentChannel)
    );
  };

  private validateAndFilterMaxSize = (files: File[]): File[] => {
    const fileSizeLimit = globalSettingStore.messengerGeneralSetting.fileSizeLimit;
    const { valid, invalid } = files.reduce(
      (acc: { valid: File[]; invalid: File[] }, file) => {
        if (file.size > fileSizeLimit) {
          acc.invalid.push(file);
        } else {
          acc.valid.push(file);
        }
        return acc;
      },
      { valid: [], invalid: [] }
    );

    this.alertInvalidImages({ invalid, maxUploadFileSize: fileSizeLimit });
    return valid;
  };

  public alertInvalidImages = ({ invalid, maxUploadFileSize }) => {
    if (invalid !== true && isEmpty(invalid)) return;
    this.actions.notificationActions.showError({
      text: i18n.t('overFileSize', {
        defaultValue: '파일의 크기가 {{size}} 이하인 파일만 전송 가능합니다.',
        size: fileSizeFilter(maxUploadFileSize, 'MB'),
      }),
    });
  };

  private uploadFiles = (fileDummyLogs: FileDummyLog[]) => {
    if (isEmpty(fileDummyLogs)) return;
    pipe<any, any, any>(
      mapIndexed(this.createQueueItem),
      forEach((item: { fileDummyLog: FileDummyLog }) =>
        promiseQueue.push(item.fileDummyLog.channelId, assoc('onError', this.onUploadError)(item))
      )
    )(fileDummyLogs);
  };

  private getUploadRequester = (fileDummyLog: FileDummyLog) => () => {
    return this.actions.proxyActions.uploadFile({
      fileDummyLog,
      onUploadProgress: this.setFileUploadProgress(fileDummyLog.id),
    });
  };

  private getReplyRequester = (targetLog, fileDummyLog: FileDummyLog) => () => {
    replyLogStore.removeReplyLog(targetLog.channelId);
    return api.file.replyWithFile({
      targetLog,
      fileDummyLog,
      onUploadProgress: this.setFileUploadProgress(fileDummyLog.id),
    });
  };

  private createQueueItem = (fileDummyLog: FileDummyLog, index: number) => {
    const replyLog = replyLogStore.getByChannelId(fileDummyLog.channelId);
    return {
      do:
        isNonNil(replyLog) && index === 0
          ? this.getReplyRequester(replyLog, fileDummyLog)
          : this.getUploadRequester(fileDummyLog),
      fileDummyLog,
    };
  };

  private onUploadError = (__, { fileDummyLog }) => {
    this.channelLogsStore.removeLog(fileDummyLog.id);
  };

  private forwardLogBeforeUpload = (channelId: string) => {
    const channelFrowardLog = forwardLogStore.getByChannelId(channelId);

    if (isNonNil(channelFrowardLog)) {
      return this.logActions.sendForwardLog(channelId, channelFrowardLog);
    }

    return Promise.resolve(null);
  };

  private createFileLogsAndUploadFilesNonCurrentChannel = (
    channelId: string,
    senderId: string,
    seq: number,
    files: File[]
  ) => {
    pipe<any, any, any>(createFileDummyLogs({ channelId, senderId, seq }), this.uploadFiles)(files);
  };

  public createFileLogsAndUploadFiles = async (channelId: CHANNEL_ID, files: File[], needCreateChannel: boolean) => {
    if (needCreateChannel) {
      await this.actions.threadChannelActions.createThreadChannel();
      channelId = threadDetailUiState.currentChannelId;
    }
    await this.verifyMeteringOver(channelId);
    const senderId = userStore.id;
    const forwardedLog = await this.forwardLogBeforeUpload(channelId);
    const seq = forwardedLog?.seq ?? channelManager.getById(channelId).seq;
    if (channelId === this.detailUiState.contentsChannelId) {
      this.createFileLogsAndUploadFilesInCurrentChannel(channelId, senderId, seq, files);
    } else {
      this.createFileLogsAndUploadFilesNonCurrentChannel(channelId, senderId, seq, files);
    }
  };

  private getUnit8ArrayTypeAndName = (unit8Array): { type: string; name: string; filename: string } => {
    return pipe<any, any, any, any>(
      omit(['buffer']),
      rename({ mimetype: 'type', filename: 'name' }),
      mergeRight({ type: 'image/png', filename: 'test.png' })
    )(unit8Array);
  };

  public uploadScreenCaptureFromNative = async (unit8Array) => {
    if (coachMarkStore.isInCoachMark) return;
    const options = this.getUnit8ArrayTypeAndName(unit8Array);
    const blob = convertUint8ArrayToBlob(unit8Array.buffer, options);
    const file = convertBlobToFile(blob);
    if (uiState.currentContext === 'thread' && threadChannelLogsStore.dummyParentLogId) {
      const channel = await this.actions.threadChannelActions.createThreadChannel();
      this.pasteFile(channel, file);
      return;
    }
    const channel = channelManager.getById(uiState.channelIdByContext);
    this.pasteFile(channel, file);
  };
}

export default UploadActions;
